import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

const SavedQueryContext = createContext(null)

export function useSavedQuery () {
  const ctx = useContext(SavedQueryContext)
  if (!ctx) {
    throw new Error('useSavedQuery must be used within a SavedQueryProvider')
  }
  return ctx
}

function SavedQueryProvider ({ children }) {
  const [currentQuery, setCurrentQuery] = useState(null)
  const [isDirty, setIsDirty] = useState(false)

  const loadQuery = useCallback((query) => {
    setCurrentQuery(query)
    setIsDirty(false)
  }, [])

  const markDirty = useCallback(() => {
    setIsDirty(true)
  }, [])

  const markClean = useCallback(() => {
    setIsDirty(false)
  }, [])

  const clearQuery = useCallback(() => {
    setCurrentQuery(null)
    setIsDirty(false)
  }, [])

  const updateCurrentQuery = useCallback((updates) => {
    setCurrentQuery(prev => prev ? { ...prev, ...updates } : null)
  }, [])

  const value = useMemo(() => ({
    currentQuery,
    isDirty,
    loadQuery,
    markDirty,
    markClean,
    clearQuery,
    updateCurrentQuery
  }), [currentQuery, isDirty, loadQuery, markDirty, markClean, clearQuery, updateCurrentQuery])

  return (
    <SavedQueryContext.Provider value={value}>
      {children}
    </SavedQueryContext.Provider>
  )
}

SavedQueryProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export default SavedQueryProvider
